/**
 * Customer List page per COMP-007 Story 3.
 * Displays paginated, searchable, filterable customer list with GDAP config.
 * Accessible to Admin and Technician roles.
 */

import { useState, useMemo } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Building2, Search, RefreshCw, Loader2 } from "lucide-react";
import { GlassCard } from "@/components/sc/glass-card";
import { StatusBadge } from "@/components/sc/status-badge";
import { EmptyState } from "@/components/sc/empty-state";
import { DataTable, type Column, type SortState } from "@/components/sc/data-table";
import { Button } from "@/components/ui/button";
import { useCustomerList, useCacheRefresh, useCacheStats } from "@/hooks/use-customers";
import type { CustomerGDAPConfig } from "@/types";

const SERVICE_TIERS = ["Enterprise", "Premium", "Standard", "Essential", "Monitoring"] as const;
const STRATEGIES = ["OPEN", "LOCKED", "TEAMS"] as const;

function relativeDate(iso: string | null): string {
  if (!iso) return "Never";
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / 86_400_000);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

const COLUMNS: Column<CustomerGDAPConfig>[] = [
  {
    key: "customerName",
    header: "Name",
    sortable: true,
    className: "max-w-[200px]",
    render: (row) => (
      <span className="font-medium truncate block" title={row.customerName}>
        {row.customerName}
      </span>
    ),
  },
  {
    key: "m365TenantId",
    header: "Tenant ID",
    className: "max-w-[300px]",
    render: (row) => (
      <span className="font-mono text-label text-sc-text-muted truncate block" title={row.m365TenantId}>
        {row.m365TenantId}
      </span>
    ),
  },
  {
    key: "serviceTier",
    header: "Service Tier",
    sortable: true,
    render: (row) => <StatusBadge status={row.serviceTier} category="tier" />,
  },
  {
    key: "accessType",
    header: "GDAP Strategy",
    sortable: true,
    render: (row) => <StatusBadge status={row.accessType} category="accessType" />,
  },
  {
    key: "maxAccessLevel",
    header: "Max Access",
    render: (row) => (
      <span className="text-body-sm text-sc-text">{row.maxAccessLevel}</span>
    ),
  },
  {
    key: "lastReviewDate",
    header: "Last Reviewed",
    sortable: true,
    render: (row) => (
      <span className="text-body-sm text-sc-text-muted">
        {relativeDate(row.lastReviewDate)}
      </span>
    ),
  },
];

export function CustomersPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [tierFilter, setTierFilter] = useState("");
  const [strategyFilter, setStrategyFilter] = useState("");
  const [sort, setSort] = useState<SortState>({
    column: "customerName",
    direction: "asc",
  });

  // Debounce search input (DV-001: 300ms)
  const debounceRef = useMemo(() => {
    let timeout: ReturnType<typeof setTimeout>;
    return (value: string) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        setDebouncedSearch(value);
        setPage(1);
      }, 300);
    };
  }, []);

  const customersQuery = useCustomerList({
    page,
    pageSize,
    search: debouncedSearch || undefined,
    tier: tierFilter || undefined,
    strategy: strategyFilter || undefined,
  });
  const cacheRefresh = useCacheRefresh();
  const cacheStats = useCacheStats();

  const customers = customersQuery.data?.items ?? [];
  const total = customersQuery.data?.total ?? 0;

  // Active filter chips
  const activeFilters: { label: string; onRemove: () => void }[] = [];
  if (tierFilter) {
    activeFilters.push({
      label: `Tier: ${tierFilter}`,
      onRemove: () => { setTierFilter(""); setPage(1); },
    });
  }
  if (strategyFilter) {
    activeFilters.push({
      label: `Strategy: ${strategyFilter}`,
      onRemove: () => { setStrategyFilter(""); setPage(1); },
    });
  }
  if (debouncedSearch) {
    activeFilters.push({
      label: `Search: "${debouncedSearch}"`,
      onRemove: () => { setSearch(""); setDebouncedSearch(""); setPage(1); },
    });
  }

  return (
    <div className="space-y-4">
      {/* Controls bar */}
      <GlassCard className="flex flex-wrap items-center gap-3 !p-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-sc-text-muted" />
          <input
            placeholder="Search customers..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              debounceRef(e.target.value);
            }}
            className="h-9 w-full rounded-input border border-sc-border bg-sc-input-bg pl-9 pr-3 text-body-sm text-sc-text placeholder:text-sc-text-muted"
          />
        </div>
        <select
          value={tierFilter}
          onChange={(e) => { setTierFilter(e.target.value); setPage(1); }}
          className="h-9 rounded-input border border-sc-border bg-sc-input-bg px-3 text-body-sm text-sc-text"
          aria-label="Filter by service tier"
        >
          <option value="">All Tiers</option>
          {SERVICE_TIERS.map((tier) => (
            <option key={tier} value={tier}>{tier}</option>
          ))}
        </select>
        <select
          value={strategyFilter}
          onChange={(e) => { setStrategyFilter(e.target.value); setPage(1); }}
          className="h-9 rounded-input border border-sc-border bg-sc-input-bg px-3 text-body-sm text-sc-text"
          aria-label="Filter by GDAP strategy"
        >
          <option value="">All Strategies</option>
          {STRATEGIES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <div className="ml-auto flex items-center gap-3">
          {cacheStats.data && (
            <span className="text-label text-sc-text-muted">
              {cacheStats.data.cachedEntries} cached
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => cacheRefresh.mutate()}
            disabled={cacheRefresh.isPending}
          >
            {cacheRefresh.isPending ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <RefreshCw size={14} />
            )}
            {cacheRefresh.isPending ? "Syncing..." : "Sync HaloPSA"}
          </Button>
        </div>
      </GlassCard>

      {/* Sync result feedback */}
      {cacheRefresh.isSuccess && (
        <p className="text-label text-sc-success">
          Synced {cacheRefresh.data.refreshed} customers from HaloPSA
        </p>
      )}
      {cacheRefresh.isError && (
        <p className="text-label text-sc-danger">
          {(cacheRefresh.error as Error).message ?? "Failed to sync from HaloPSA"}
        </p>
      )}

      {/* Active filter chips */}
      {activeFilters.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {activeFilters.map((filter) => (
            <button
              key={filter.label}
              onClick={filter.onRemove}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-label bg-sc-success-bg text-sc-success hover:bg-sc-success/20 transition-colors"
            >
              {filter.label}
              <span aria-label={`Remove filter: ${filter.label}`}>&times;</span>
            </button>
          ))}
        </div>
      )}

      {/* Table */}
      <DataTable<CustomerGDAPConfig>
        columns={COLUMNS}
        data={customers}
        total={total}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
        sort={sort}
        onSortChange={setSort}
        isLoading={customersQuery.isLoading}
        rowKey={(row) => row.m365TenantId}
        onRowClick={(row) => {
          void navigate({ to: "/customers/$customerId", params: { customerId: row.m365TenantId } });
        }}
        emptyState={
          <EmptyState
            icon={<Building2 size={48} />}
            heading="No customers configured"
            description="Customer data syncs from HaloPSA. Check your HaloPSA integration settings."
          />
        }
      />
    </div>
  );
}
